import { HocuspocusProvider, HocuspocusProviderWebsocket } from "@hocuspocus/provider";
import { IndexeddbPersistence } from "y-indexeddb";
import * as Y from "yjs";
import {
  can,
  collectSubtree,
  COMMENTS_FIELD,
  FILE_TEXT_FIELD,
  fileDoc,
  isSameOrDescendant,
  isSecretPath,
  lightColor,
  newFileId,
  nodesOf,
  readNodes,
  resolveTree,
  SUGGESTIONS_FIELD,
  suggestionsDoc,
  treeDoc,
  validateNodeName,
  type Activity,
  type BlobRef,
  type CommentThread,
  type NodeType,
  type PresenceState,
  type PresenceUser,
  type Role,
  type Suggestion,
  type SuggestionRequest,
  type SuggestionResponse,
  type TreeNode,
} from "@ide/shared";
import { Emitter } from "./emitter";

export interface RoomTicket {
  token: string;
  role: Role;
  user: { id: string; name: string; handle: string; avatarUrl: string | null; color: string };
}

export interface SessionOptions {
  projectId: string;
  syncUrl: string;
  /** Busca um token de sala novo (API no navegador; ponte nativa no desktop). */
  fetchTicket: () => Promise<RoomTicket>;
  /** Guarda o estado no IndexedDB para editar sem conexão (princípio 4). */
  offline?: boolean;
  /**
   * false: sessão só de leitura em segundo plano (ex.: execução local mantendo os arquivos em
   * dia), sem aparecer na presença nem nos cursores do time.
   */
  presence?: boolean;
}

export type ConnectionStatus = "connecting" | "connected" | "disconnected";

export interface TreeSnapshot {
  nodes: Map<string, TreeNode>;
  paths: Map<string, string>;
  children: Map<string | null, TreeNode[]>;
}

export interface Peer {
  clientId: number;
  user: PresenceUser;
  activity: Activity;
  currentFile: string | null;
  needsHelp: boolean;
  isSelf: boolean;
}

export interface FileHandle {
  fileId: string;
  doc: Y.Doc;
  text: Y.Text;
  provider: HocuspocusProvider;
  /** Resolve quando o conteúdo inicial chegou (do servidor ou do cache local). */
  ready: Promise<void>;
  /** Resolve quando o conteúdo do servidor chegou (o cache local pode estar vazio ou atrasado). */
  synced: Promise<void>;
  release(): void;
}

export interface SuggestionsHandle {
  fileId: string;
  doc: Y.Doc;
  provider: HocuspocusProvider;
  list(): Suggestion[];
  /** Comentários na linha (conversas), mais antigos primeiro. */
  comments(): CommentThread[];
  onChange(fn: () => void): () => void;
  onCommentsChange(fn: () => void): () => void;
  request(body: DistributiveOmit<SuggestionRequest, "requestId">): Promise<SuggestionResponse>;
  release(): void;
}

type DistributiveOmit<T, K extends keyof never> = T extends unknown ? Omit<T, K> : never;

type SessionEvents = {
  status: ConnectionStatus;
  tree: TreeSnapshot;
  presence: Peer[];
  role: Role;
  denied: string;
  /** Sem a permissão "Ver .env" para este arquivo (id): a cópia local dele foi descartada. */
  secret: string;
  unsynced: number;
  /** Por que a sala não abre (ex.: o token de acesso falhou); null quando está tudo bem. */
  trouble: string | null;
};

interface RefCounted<T> {
  value: T;
  refs: number;
  destroy: () => void;
}

/**
 * Sessão de colaboração de um projeto (seção 4):
 *  - UM WebSocket compartilhado por todos os documentos (árvore + arquivos abertos);
 *  - token curto pedido a cada (re)conexão — o papel pode mudar no meio da sessão;
 *  - estado local no IndexedDB: continua editando sem rede e sincroniza depois.
 */
export class ProjectSession extends Emitter<SessionEvents> {
  readonly socket: HocuspocusProviderWebsocket;
  readonly treeDoc = new Y.Doc();
  readonly treeProvider: HocuspocusProvider;
  private treePersistence: IndexeddbPersistence | null = null;
  private files = new Map<string, RefCounted<FileHandle>>();
  private suggestionDocs = new Map<string, RefCounted<SuggestionsHandle>>();
  private ticket: RoomTicket | null = null;
  private ticketPromise: Promise<RoomTicket> | null = null;
  private ticketAt = 0;
  private _status: ConnectionStatus = "connecting";
  private _role: Role | null = null;
  private destroyed = false;
  private recoveryListenersAttached = false;
  private localPresence: PresenceState = { activity: "editing", currentFile: null, needsHelp: false };
  private readonly onOnline = () => this.recoverConnection();
  private readonly onVisibilityChange = () => {
    if (document.visibilityState === "visible") this.recoverConnection();
  };

  constructor(private readonly opts: SessionOptions) {
    super();
    this.socket = new HocuspocusProviderWebsocket({
      url: opts.syncUrl,
      onStatus: ({ status }) => {
        this._status = status as ConnectionStatus;
        // Depois de uma queda (ex.: papel alterado), o próximo token precisa ser novo.
        if (status !== "connected") this.ticketAt = 0;
        else this.setTrouble(null);
        this.emit("status", this._status);
      },
    });
    this.treeProvider = this.createProvider(treeDoc(opts.projectId), this.treeDoc, opts.presence !== false);
    nodesOf(this.treeDoc).observe(() => this.emit("tree", this.tree()));
    this.treeProvider.awareness?.on("change", () => this.emit("presence", this.peers()));
    this.treeProvider.on("unsyncedChanges", ({ number }: { number: number }) =>
      this.emit("unsynced", number),
    );
    // Snapshots iniciais estáveis: `latest("tree")`/`latest("presence")` nunca são undefined.
    this.remember("tree", this.tree());
    this.remember("presence", this.peers());
    this.remember("trouble", null);
  }

  private setTrouble(reason: string | null) {
    if (this.latest("trouble") !== reason) this.emit("trouble", reason);
  }

  get status() {
    return this._status;
  }

  /**
   * Conteúdo confiável de um arquivo aberto: o do servidor quando conectado (o cache local de um
   * arquivo nunca aberto nesta máquina chega vazio); sem conexão, o do cache.
   */
  /** Como `loaded`, mas diz se o conteúdo chegou mesmo (false: venceu o tempo, texto desconhecido). */
  loadedOk(handle: FileHandle, timeoutMs = 20_000): Promise<boolean> {
    const source = this._status === "connected" ? handle.synced : handle.ready;
    return Promise.race([
      source.then(() => true),
      new Promise<boolean>((r) => setTimeout(() => r(false), timeoutMs)),
    ]);
  }

  loaded(handle: FileHandle, timeoutMs = 10_000): Promise<void> {
    const source = this._status === "connected" ? handle.synced : handle.ready;
    return Promise.race([source, new Promise<void>((r) => setTimeout(r, timeoutMs))]);
  }

  /** Resolve quando a árvore de arquivos chegou do servidor. */
  whenTreeSynced(timeoutMs?: number): Promise<void> {
    if (this.treeProvider.isSynced) return Promise.resolve();
    return new Promise((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      let settled = false;
      const cleanup = () => {
        if (timer) clearTimeout(timer);
        this.treeProvider.off("synced", done);
      };
      const done = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve();
      };
      this.treeProvider.on("synced", done);
      if (timeoutMs !== undefined) {
        timer = setTimeout(() => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(new Error("A árvore de arquivos do projeto não sincronizou a tempo."));
        }, timeoutMs);
      }
      if (this.treeProvider.isSynced) done();
    });
  }

  get role(): Role | null {
    return this._role;
  }

  get user() {
    return this.ticket?.user ?? null;
  }

  get canWrite() {
    return can(this._role, "write");
  }

  private localName(documentName: string) {
    return `kompil:${this.ticket?.user.id ?? "anon"}:${documentName}`;
  }

  /** Busca o primeiro token (descobre papel e usuário) e conecta. */
  async start(): Promise<void> {
    try {
      await this.getTicket();
    } catch (err) {
      // Sem rede: abre com o papel e o usuário da última vez e o que está salvo no aparelho
      // (IndexedDB). As edições sincronizam quando a conexão voltar.
      const cached = this.opts.offline ? readCachedTicket(this.opts.projectId) : null;
      if (!cached) throw err;
      this.ticket = { ...cached, token: "" };
      this.ticketPromise = null;
      this.ticketAt = 0;
      this._role = cached.role;
      this.emit("role", cached.role);
    }
    this.attachRecoveryListeners();
    // O nome do cache inclui o id do usuário (máquinas compartilhadas), por isso só agora.
    if (this.opts.offline)
      this.treePersistence = new IndexeddbPersistence(
        this.localName(treeDoc(this.opts.projectId)),
        this.treeDoc,
      );
    this.treeProvider.attach();
    this.applyLocalUser();
  }

  private attachRecoveryListeners() {
    if (this.recoveryListenersAttached || typeof window === "undefined") return;
    window.addEventListener("online", this.onOnline);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.recoveryListenersAttached = true;
  }

  /** Reconecta se caiu (ou confere a conexão): volta da rede, aba visível, botão Atualizar. */
  recoverConnection() {
    if (this.destroyed || !navigator.onLine || document.visibilityState === "hidden") return;
    if (this._status === "connected") {
      this.socket.checkConnection();
      return;
    }
    // Conectando há tempo demais ou caiu: token novo e uma conexão do zero (antes, "conectando"
    // não fazia nada e o aviso girava para sempre).
    this.ticketAt = 0;
    this.ticketPromise = null;
    this.socket.disconnect();
    void this.socket.connect();
  }

  private async getTicket(): Promise<RoomTicket> {
    // Pedidos simultâneos (vários documentos reconectando) compartilham a mesma busca.
    if (this.ticketPromise && Date.now() - this.ticketAt < 5_000) return this.ticketPromise;
    this.ticketAt = Date.now();
    this.ticketPromise = this.opts.fetchTicket().then((t) => {
      this.ticket = t;
      if (this.opts.offline) saveCachedTicket(this.opts.projectId, t);
      if (t.role !== this._role) {
        this._role = t.role;
        this.emit("role", t.role);
      }
      return t;
    });
    return this.ticketPromise;
  }

  private createProvider(
    name: string,
    document: Y.Doc,
    withAwareness = true,
    onDenied?: (reason: string) => void,
  ): HocuspocusProvider {
    const provider = new HocuspocusProvider({
      websocketProvider: this.socket,
      name,
      document,
      // Cada instância tem a própria rota no socket compartilhado: fechar e reabrir o
      // mesmo arquivo (ou abri-lo em dois lugares) não disputa a conexão anterior.
      sessionAwareness: true,
      ...(withAwareness ? {} : { awareness: null }),
      token: async () => {
        try {
          return (await this.getTicket()).token;
        } catch (err) {
          // Sem token não há sala: registra o motivo (a barra mostra em vez de girar sem fim).
          this.ticketPromise = null;
          this.setTrouble(err instanceof Error ? err.message : "Não foi possível pedir acesso à sala.");
          throw err;
        }
      },
      onAuthenticationFailed: ({ reason }) => {
        if (reason === "role-changed") {
          // Papel mudou entre a emissão do token e a conexão: pede outro e tenta de novo.
          this.ticketAt = 0;
          setTimeout(() => {
            if (this.destroyed) return;
            provider.detach();
            provider.attach();
          }, 300);
          return;
        }
        if (onDenied) onDenied(reason);
        else this.emit("denied", reason);
      },
    });
    return provider;
  }

  /** O arquivo é um .env (pelo nome na árvore)? */
  private isSecretFile(fileId: string): boolean {
    const name = readNodes(nodesOf(this.treeDoc)).get(fileId)?.name;
    return Boolean(name && isSecretPath(name));
  }

  /** Apaga a cópia guardada no aparelho de um documento (IndexedDB). */
  private dropLocalCopy(documentName: string) {
    try {
      indexedDB.deleteDatabase(this.localName(documentName));
    } catch {
      // sem IndexedDB
    }
  }

  private applyLocalUser() {
    const u = this.ticket?.user;
    if (!u) return;
    const user: PresenceUser = {
      id: u.id,
      name: u.name,
      handle: u.handle,
      avatarUrl: u.avatarUrl,
      color: u.color,
      colorLight: lightColor(u.color),
      role: this._role ?? "viewer",
    };
    this.treeProvider.awareness?.setLocalState({ ...this.localPresence, user });
    for (const f of this.files.values()) f.value.provider.awareness?.setLocalStateField("user", user);
  }

  // ───────────── Árvore de arquivos ─────────────

  tree(): TreeSnapshot {
    const nodes = readNodes(nodesOf(this.treeDoc));
    const { paths, children } = resolveTree(nodes);
    return { nodes, paths, children };
  }

  private requireWrite() {
    if (!this.canWrite) throw new Error("Seu papel não permite editar a árvore de arquivos.");
  }

  /**
   * Tem a permissão "Ver .env" (o projeto aberto informa). Sem ela, os arquivos .env não podem ser
   * criados, renomeados, movidos nem apagados por aqui — só o dono (ou quem ele liberou) mexe neles.
   */
  private envAllowed = true;

  setEnvAllowed(allowed: boolean) {
    this.envAllowed = allowed;
  }

  private requireEnv(...names: Array<string | undefined>) {
    if (!this.envAllowed && names.some((n) => n && isSecretPath(n)))
      throw new Error("Só quem tem a permissão “Ver .env” mexe nos arquivos .env deste projeto.");
  }

  /** Nomes dos arquivos dentro de um item (o próprio, ou tudo dentro da pasta). */
  private namesUnder(id: string): string[] {
    const nodes = readNodes(nodesOf(this.treeDoc));
    return collectSubtree(nodes, id).map((x) => nodes.get(x)?.name ?? "");
  }

  createNode(parentId: string | null, rawName: string, type: NodeType): string {
    this.requireWrite();
    const name = rawName.trim();
    const error = validateNodeName(name);
    if (error) throw new Error(error);
    if (type === "file") this.requireEnv(name);
    const { children } = this.tree();
    if ((children.get(parentId) ?? []).some((n) => n.name === name))
      throw new Error(`Já existe "${name}" nesta pasta.`);
    const id = newFileId();
    nodesOf(this.treeDoc).set(id, { id, name, parentId, type, createdAt: Date.now() });
    return id;
  }

  renameNode(id: string, rawName: string) {
    this.requireWrite();
    const name = rawName.trim();
    const error = validateNodeName(name);
    if (error) throw new Error(error);
    const map = nodesOf(this.treeDoc);
    const node = map.get(id);
    if (!node) throw new Error("Item não encontrado.");
    this.requireEnv(name, node.name);
    const siblings = this.tree().children.get(node.parentId) ?? [];
    if (siblings.some((n) => n.id !== id && n.name === name))
      throw new Error(`Já existe "${name}" nesta pasta.`);
    map.set(id, { ...node, name });
  }

  /**
   * Marca o arquivo como binário (o conteúdo fica no armazenamento de arquivos do projeto) ou
   * volta a ser texto (`null`).
   */
  setFileBlob(id: string, blob: BlobRef | null) {
    this.requireWrite();
    const map = nodesOf(this.treeDoc);
    const node = map.get(id);
    if (!node || node.type !== "file") throw new Error("Arquivo não encontrado.");
    this.requireEnv(node.name);
    if (!blob) {
      if (!node.blob) return;
      const { blob: _old, ...rest } = node;
      map.set(id, rest);
      return;
    }
    if (node.blob?.sha256 === blob.sha256 && node.blob.size === blob.size) return;
    map.set(id, { ...node, blob: { sha256: blob.sha256, size: blob.size } });
  }

  moveNode(id: string, parentId: string | null) {
    this.requireWrite();
    const map = nodesOf(this.treeDoc);
    const node = map.get(id);
    if (!node) throw new Error("Item não encontrado.");
    const nodes = readNodes(map);
    if (parentId && isSameOrDescendant(nodes, parentId, id))
      throw new Error("Não dá para mover uma pasta para dentro dela mesma.");
    if (parentId && nodes.get(parentId)?.type !== "folder")
      throw new Error("O destino precisa ser uma pasta.");
    this.requireEnv(node.name);
    map.set(id, { ...node, parentId });
  }

  deleteNode(id: string) {
    this.requireWrite();
    this.requireEnv(...this.namesUnder(id));
    const map = nodesOf(this.treeDoc);
    const ids = collectSubtree(readNodes(map), id);
    this.treeDoc.transact(() => ids.forEach((x) => map.delete(x)));
  }

  // ───────────── Arquivos ─────────────

  /** Abre (ou reaproveita) o documento de um arquivo. Chame release() ao fechar. */
  openFile(fileId: string): FileHandle {
    const existing = this.files.get(fileId);
    if (existing) {
      existing.refs++;
      return existing.value;
    }
    const doc = new Y.Doc();
    const name = fileDoc(this.opts.projectId, fileId);
    // .env nunca fica guardado no aparelho: sem a permissão, nada para editar escondido e reenviar
    // depois; com ela, o conteúdo vem sempre do servidor (o que o dono decidiu).
    const secret = this.isSecretFile(fileId);
    if (secret) this.dropLocalCopy(name);
    const provider = this.createProvider(name, doc, this.opts.presence !== false, (reason) => {
      if (reason !== "secret") return this.emit("denied", reason);
      // Perdeu (ou não tem) a permissão "Ver .env": o que ficou no aparelho some, e nada do que
      // for digitado vale — quando a permissão voltar, o arquivo abre de novo do servidor.
      this.dropLocalCopy(name);
      doc.transact(() => {
        const text = doc.getText(FILE_TEXT_FIELD);
        if (text.length) text.delete(0, text.length);
      }, provider);
      this.emit("secret", fileId);
    });
    const persistence =
      this.opts.offline && this.canWrite && !secret
        ? new IndexeddbPersistence(this.localName(name), doc)
        : null;
    const synced = new Promise<void>((resolve) => {
      if (provider.isSynced) return resolve();
      provider.on("synced", () => resolve());
    });
    const ready = new Promise<void>((resolve) => {
      void synced.then(resolve);
      persistence?.whenSynced.then(() => resolve());
    });
    provider.attach();
    const handle: FileHandle = {
      fileId,
      doc,
      text: doc.getText(FILE_TEXT_FIELD),
      provider,
      ready,
      synced,
      release: () => this.releaseFile(fileId),
    };
    this.files.set(fileId, {
      value: handle,
      refs: 1,
      destroy: () => {
        provider.destroy();
        void persistence?.destroy();
        doc.destroy();
      },
    });
    this.applyLocalUser();
    return handle;
  }

  private releaseFile(fileId: string) {
    const entry = this.files.get(fileId);
    if (!entry) return;
    entry.refs--;
    if (entry.refs <= 0) {
      entry.destroy();
      this.files.delete(fileId);
    }
  }

  // ───────────── Sugestões (modo navegador/mentor) ─────────────

  openSuggestions(fileId: string): SuggestionsHandle {
    const existing = this.suggestionDocs.get(fileId);
    if (existing) {
      existing.refs++;
      return existing.value;
    }
    const doc = new Y.Doc();
    const provider = this.createProvider(suggestionsDoc(this.opts.projectId, fileId), doc, false);
    const pending = new Map<string, (r: SuggestionResponse) => void>();
    provider.on("stateless", ({ payload }: { payload: string }) => {
      try {
        const msg = JSON.parse(payload) as SuggestionResponse;
        if (msg.type === "suggestion.result") {
          pending.get(msg.requestId)?.(msg);
          pending.delete(msg.requestId);
        }
      } catch {
        // mensagem de outro tipo
      }
    });
    provider.attach();
    const map = doc.getMap<Suggestion>(SUGGESTIONS_FIELD);
    const comments = doc.getMap<CommentThread>(COMMENTS_FIELD);
    const handle: SuggestionsHandle = {
      fileId,
      doc,
      provider,
      list: () => [...map.values()].sort((a, b) => b.createdAt - a.createdAt),
      onChange: (fn) => {
        map.observe(fn);
        return () => map.unobserve(fn);
      },
      comments: () => [...comments.values()].sort((a, b) => a.createdAt - b.createdAt),
      onCommentsChange: (fn) => {
        comments.observe(fn);
        return () => comments.unobserve(fn);
      },
      request: (body) =>
        new Promise<SuggestionResponse>((resolve) => {
          const requestId = Math.random().toString(36).slice(2) + Date.now().toString(36);
          const timer = setTimeout(() => {
            pending.delete(requestId);
            resolve({
              type: "suggestion.result",
              requestId,
              ok: false,
              error: "Sem resposta do servidor. Tente de novo.",
            });
          }, 10_000);
          pending.set(requestId, (r) => {
            clearTimeout(timer);
            resolve(r);
          });
          provider.sendStateless(JSON.stringify({ ...body, requestId }));
        }),
      release: () => {
        const entry = this.suggestionDocs.get(fileId);
        if (!entry) return;
        entry.refs--;
        if (entry.refs <= 0) {
          entry.destroy();
          this.suggestionDocs.delete(fileId);
        }
      },
    };
    this.suggestionDocs.set(fileId, {
      value: handle,
      refs: 1,
      destroy: () => {
        provider.destroy();
        doc.destroy();
      },
    });
    return handle;
  }

  // ───────────── Presença (awareness) ─────────────

  /** Atualiza a presença local. Nunca inclui conteúdo do terminal (seção 4.3). */
  setPresence(patch: Partial<Pick<PresenceState, "activity" | "currentFile" | "needsHelp">>) {
    this.localPresence = { ...this.localPresence, ...patch };
    const awareness = this.treeProvider.awareness;
    if (!awareness) return;
    for (const [key, value] of Object.entries(patch)) awareness.setLocalStateField(key, value);
  }

  get presence() {
    return this.localPresence;
  }

  peers(): Peer[] {
    const awareness = this.treeProvider.awareness;
    if (!awareness) return [];
    const self = awareness.clientID;
    const selfUser = (awareness.getLocalState() as PresenceState | null)?.user?.id ?? null;
    // A mesma pessoa em várias janelas aparece uma vez: a conexão mais ativa (não ausente) vence e,
    // entre iguais, a mais recente. As suas outras janelas também são "você".
    const best = new Map<string, Peer>();
    const rank = (p: Peer) => (p.activity === "idle" ? 1 : 0);
    const states = [...awareness.getStates().entries()].sort((a, b) => b[0] - a[0]);
    for (const [clientId, state] of states) {
      const s = state as PresenceState;
      if (!s.user?.id) continue;
      const peer: Peer = {
        clientId,
        user: s.user,
        activity: s.activity ?? "editing",
        currentFile: s.currentFile ?? null,
        needsHelp: Boolean(s.needsHelp),
        isSelf: clientId === self || s.user.id === selfUser,
      };
      const prev = best.get(s.user.id);
      if (!prev || rank(peer) < rank(prev) || (clientId === self && rank(peer) === rank(prev)))
        best.set(s.user.id, peer);
    }
    const out = [...best.values()];
    return out.sort((a, b) => Number(b.isSelf) - Number(a.isSelf) || a.user.name.localeCompare(b.user.name));
  }

  destroy() {
    this.destroyed = true;
    if (this.recoveryListenersAttached) {
      window.removeEventListener("online", this.onOnline);
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
      this.recoveryListenersAttached = false;
    }
    for (const f of this.files.values()) f.destroy();
    for (const s of this.suggestionDocs.values()) s.destroy();
    this.files.clear();
    this.suggestionDocs.clear();
    this.treeProvider.destroy();
    void this.treePersistence?.destroy();
    this.socket.destroy();
    this.treeDoc.destroy();
    this.clearListeners();
  }
}

/** Papel e usuário da última sala (nunca o token), para abrir o projeto sem rede. */
const ticketKey = (projectId: string) => `kompil.sala.${projectId}`;

function saveCachedTicket(projectId: string, t: RoomTicket) {
  try {
    localStorage.setItem(ticketKey(projectId), JSON.stringify({ role: t.role, user: t.user }));
  } catch {
    // sem armazenamento
  }
}

function readCachedTicket(projectId: string): Omit<RoomTicket, "token"> | null {
  try {
    const raw = JSON.parse(localStorage.getItem(ticketKey(projectId)) ?? "null") as Omit<
      RoomTicket,
      "token"
    > | null;
    return raw?.role && raw.user?.id ? raw : null;
  } catch {
    return null;
  }
}

/** Privacidade: apaga o cache local de documentos (ao sair da conta). */
export async function clearLocalCollabData(): Promise<void> {
  // Leituras guardadas para abrir sem internet (conta, projetos) também saem.
  navigator.serviceWorker?.controller?.postMessage("kompil:clear");
  try {
    for (const key of Object.keys(localStorage))
      if (key.startsWith("kompil.sala.")) localStorage.removeItem(key);
  } catch {
    // sem armazenamento
  }
  if (!("databases" in indexedDB)) return;
  const dbs = await indexedDB.databases();
  await Promise.all(
    dbs
      .filter((d) => d.name?.startsWith("kompil:"))
      .map(
        (d) =>
          new Promise<void>((resolve) => {
            const req = indexedDB.deleteDatabase(d.name!);
            req.onsuccess = req.onerror = req.onblocked = () => resolve();
          }),
      ),
  );
}
